import { Link, useParams } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { useFetch } from "@/lib/useFetch";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, absUrl, SITE_URL } from "@/lib/api";
import { Seo, faqLd, breadcrumbLd } from "@/components/site/Seo";
import { PageHero, CtaBand, FaqList, ContactAside } from "@/components/site/Common";
import { Sections } from "@/components/site/RichText";
import { Loading } from "@/pages/Pages";
import NotFound from "@/pages/NotFound";

const day = (iso) => (iso ? new Date(iso).toLocaleDateString("tr-TR", { dateStyle: "long" }) : "");

const BLOG_CRUMBS = [{ name: "Blog" }];
const FAQ_CRUMBS = [{ name: "SSS" }];

export function Blog() {
  const { settings: s } = useSite();
  const { data, loading } = useFetch("/public/posts");
  return (
    <>
      <Seo title={s.seo?.blog?.title} description={s.seo?.blog?.description} path="/blog" />
      <PageHero title="Bilgi Merkezi" subtitle="Güvenlik planlaması, risk değerlendirmesi ve hizmet seçimi üzerine rehber yazılar." image="/img/consulting.webp" crumbs={BLOG_CRUMBS} />
      <section className="bg-zinc-50 py-14 sm:py-20">
        <div className="container-x">
          {loading ? <Loading /> : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
              {(data || []).map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white transition-[box-shadow,border-color] hover:border-[color:var(--red)] hover:shadow-lg" data-testid={`blog-card-${p.slug}`}>
                  <div className="aspect-[16/9] overflow-hidden bg-zinc-200">
                    {p.image && <img src={mediaUrl(p.image)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span className="flex items-center gap-1.5 text-xs text-zinc-500"><Calendar className="h-3.5 w-3.5" /> {day(p.published_at)}</span>
                    <h2 className="mt-2 text-base font-bold leading-snug text-zinc-950 sm:text-lg">{p.title}</h2>
                    <p className="mt-2 flex-1 text-sm text-zinc-600">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[color:var(--red)]">Devamını oku <ArrowRight className="h-4 w-4" /></span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { data: p, loading, error } = useFetch(`/public/posts/${slug}`);
  if (loading) return <Loading />;
  if (error || !p) return <NotFound />;
  const path = `/blog/${p.slug}`;
  const articleLd = {
    "@context": "https://schema.org", "@type": "Article", headline: p.title, description: p.excerpt,
    image: absUrl(p.image), datePublished: p.published_at, mainEntityOfPage: `${SITE_URL}${path}`,
    author: { "@type": "Organization", name: "Rüzgar Özel Güvenlik" },
    publisher: { "@type": "Organization", name: "Rüzgar Özel Güvenlik", logo: { "@type": "ImageObject", url: absUrl("/assets/logo-shield.png") } },
  };
  return (
    <>
      <Seo title={p.seo_title} description={p.seo_description} path={path} image={p.image}
        jsonLd={[articleLd, breadcrumbLd([{ name: "Ana Sayfa", path: "/" }, { name: "Blog", path: "/blog" }, { name: p.title, path }])]} />
      <PageHero title={p.title} subtitle={p.excerpt} image={p.image} crumbs={[{ name: "Blog", path: "/blog" }, { name: p.title }]} eyebrow={day(p.published_at)} />
      <div className="container-x grid gap-12 py-14 sm:py-20 lg:grid-cols-[1fr_340px]">
        <article className="max-w-3xl" data-testid="blog-article"><Sections sections={p.sections} /></article>
        <div className="lg:sticky lg:top-24 lg:self-start"><ContactAside /></div>
      </div>
      <CtaBand />
    </>
  );
}

export function Faq() {
  const { settings: s } = useSite();
  const { data, loading } = useFetch("/public/faqs");
  return (
    <>
      <Seo title={s.seo?.faq?.title} description={s.seo?.faq?.description} path="/sikca-sorulan-sorular" jsonLd={faqLd(data || [])} />
      <PageHero title="Sıkça Sorulan Sorular" subtitle="Talep süreci, planlama ve teklif hakkında merak edilenler." image="/img/guard.webp" crumbs={FAQ_CRUMBS} />
      <div className="bg-zinc-50">
        <div className="container-x grid gap-12 py-14 sm:py-20 lg:grid-cols-[1fr_340px]">
          <div>{loading ? <Loading /> : <FaqList faqs={data || []} testid="faq-page-list" />}
            <p className="mt-8 text-sm text-zinc-600">Sorunuzun cevabını bulamadınız mı? <Link to="/teklif-al" className="font-bold text-[color:var(--red)] underline">Teklif formunu</Link> doldurun, sizi arayalım.</p>
          </div>
          <div className="lg:sticky lg:top-24 lg:self-start"><ContactAside /></div>
        </div>
      </div>
    </>
  );
}
